import { Dispatch, RefObject } from "react";
import { AdministrativeLevels, EarlyWarningTypes, FlatAdmin, FLWDataRefType, HRWDataRefType, HWNDataRefType, LIGDataRefType, LSWDataRefType, MWADataRefType, NTWDataRefType } from "./CreateWarningTypes";
import { useTheme } from "../../context/ThemeContext";
import { ThemeContextType } from "../../context/ContextTypes";
import { APICallNewHRWRequest, APIRequestAddHRWAreaTypes, APIRequestAddHRWTypes, APIRequestSelectedSubscribersType, ResponseTypes } from "../../services/APIEarlyWarningService";

export const flatMap = (data: AdministrativeLevels, selectedArea: string[] = [], depth: number = 0): FlatAdmin[] => {
    const result: FlatAdmin[] = [];

    for(const [code, value] of Object.entries(data)) {
        if (typeof value === "string") {
            result.push({code, name: value, adminLevel: value, depth})
        } else {
            result.push({code, name: value.name, adminLevel: value.adminLevel, depth})

            if (value.children && !selectedArea.includes(code)) {
                const children = flatMap(value.children, selectedArea, depth + 1)
                result.push(...children)
            }
        }
    }
    return result
}

export const paginationList = (data: FlatAdmin[], size: number): FlatAdmin[][] => {
    const result: FlatAdmin[][] = []

    if (size >= data.length) {
        return [data]
    }

    let start = 0

    while (start < data.length) {
        result.push(data.slice(start, start + size))
        start += size
    }

    return result
}

// Calculate field count
export const sideFieldCount = (totalField: number): number => {
    if (totalField > 0) {
        const value = totalField % 2

        if (value === 0){
            return totalField / 2
        } else {
            return (totalField + 1) /2
        }
    } else {
        return totalField
    }
}

export const getThemeColors = (theme: ThemeContextType) => {
        switch (theme.theme) {
            case 'dark':
                return {
                    background: '#00497eff',
                    stroke: 'black',
                    defaultFill: '#EEE',
                    selectStroke: "red",
                    disable: '#a3a3a3',
                    disableStroke: '#4c4c4c'
                };
            case 'light':
                return {
                    background: '#E6F2FF',
                    stroke: 'black',
                    defaultFill: '#EEE',
                    selectStroke: "red",
                    disable: '#a3a3a3',
                    disableStroke: '#4c4c4c'
                };
            case 'colorful':
            default:
                return {
                    background: '#e8f4f8',
                    stroke: 'black',
                    defaultFill: '#EEE',
                    selectStroke: "red",
                    disable: '#a3a3a3',
                    disableStroke: '#4c4c4c'
                }
        }
    }

// export function convertToOffsetDateTime(localDateTimeString: string) {
//     const localDate = new Date(localDateTimeString);
//     const offset = -localDate.getTimezoneOffset();
//     const sign = offset >= 0 ? "+" : "-";
//     const pad = (num: number) => String(Math.floor(Math.abs(num))).padStart(2, "0");

//     const hours = pad(offset / 60);
//     const minutes = pad(offset % 60);

//     const isoString = localDate.toISOString().split('.')[0]; // remove milliseconds
//     return `${isoString}${sign}${hours}:${minutes}`;
// }
export function convertToOffsetDateTime(localDateTimeString: string): string {
    const date = new Date(localDateTimeString);

    const pad = (n: number) => n.toString().padStart(2, '0');

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());

    const offsetMinutes = -date.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const offsetHours = pad(Math.floor(Math.abs(offsetMinutes) / 60));
    const offsetMins = pad(Math.abs(offsetMinutes) % 60);

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetMins}`;
}

export const formatDate = (date: Date): string => {
    const pad = (n: number) => n.toString().padStart(2, "0");
    return (
        date.getFullYear() +
        "-" +
        pad(date.getMonth() + 1) +
        "-" +
        pad(date.getDate()) +
        "T" +
        pad(date.getHours()) +
        ":" +
        pad(date.getMinutes())
    )
}

export const getSeverityColor = (name: string): string => {
    switch (name.toLocaleLowerCase()) {
        case "yellow": return "#ffff00"
        case "amber": return "#ffbf00"
        case "red": return "#ff0000"
        case "white": return "#ffffff"
        case "": return "#00ff00"
        default: return ""
    }
}

export type SubscriberDataType = {
    name: string
    whatsappContact?: string
    whatsapp: boolean
    smsContact?: string
    sms: boolean
    viberContact?: string
    viber: boolean
    email: string
}

export const parseCSVManually = (text: string): SubscriberDataType[] => {
    const lines = text.split('\n').filter(line => line.trim())
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase())
    
    return lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim())
        const row: any = {};
        
        headers.forEach((header, index) => {
            row[header] = values[index]
        });
        
        return {
            name: row.name || '',
            contact: row.contact || '',
            whatsappContact: row.whatsapp_contact || undefined,
            whatsapp: row.whatsapp === 'y' || row.whatsapp === 'Y' ? true : row.whatsapp === 'n' || row.whatsapp === 'N' ? false : false,
            smsContact: row.sms_contact || undefined,
            sms: row.sms === 'y' || row.sms === 'Y' ? true : row.sms === 'n' || row.sms === 'N' ? false : false,
            viberContact: row.viber_contact || undefined,
            viber: row.viber === 'y' || row.viber === 'Y' ? true : row.viber === 'n' || row.viber === 'N' ? false : false,
            email: row.email || ''
        }
    })
}

// const parseBoolean = (value: any): boolean => {
//     if (typeof value === 'boolean') return value
//     if (typeof value === 'string') 
//     {
//         return value.toLowerCase() === 'true' || value === '1'
//     }
//     return false
// }

// // Validate CSV format
// export const validateCSVFormat = (text: string): { valid: boolean, error?: string } => {
//     const lines = text.split('\n').filter(line => line.trim())
//     const headers = lines[0].split(',').map(h => h.trim().toLowerCase())

//     const requiredHeaders = ['name', 'whatsapp_contact', 'whatsapp', 'sms_contact', 'sms', 'viber_contact', 'viber', 'email']
//     const missingHeaders = requiredHeaders.filter(header => !headers.includes(header))

//     if (missingHeaders.length > 0) 
//     {
//         return { valid: false, error: `Missing required headers: ${missingHeaders.join(', ')}` }
//     }

//     return { valid: true }
// }

// Type definitions
interface ValidationResult {
    valid: boolean;
    error?: string;
}

interface CSVHeaders {
    name: number;
    whatsapp_contact: number;
    sms_contact: number;
    viber_contact: number;
    whatsapp: number;
    sms: number;
    viber: number;
    email: number;
}

export const validateCSVFormat = (text: string): ValidationResult => {
    const lines: string[] = text
        .split('\n')
        .map(line => line.trim())
        .filter((line): line is string => line.length > 0);
    
    if (lines.length === 0) {
        return { valid: false, error: 'CSV file is empty' };
    }

    // Parse and normalize headers
    const headers: string[] = parseCSVLine(lines[0]).map(h => h.trim().toLowerCase());
    const requiredHeaders: (keyof CSVHeaders)[] = [
        'name', 'whatsapp_contact', 'sms_contact', 'viber_contact',
        'whatsapp', 'sms', 'viber', 'email'
    ];
    
    // Check for missing required headers
    const missingHeaders = requiredHeaders.filter(header => !headers.includes(header));
    if (missingHeaders.length > 0) {
        return { 
            valid: false, 
            error: `Missing required headers: ${missingHeaders.join(', ')}` 
        };
    }

    // Build typed header index map
    const headerIndex: Partial<Record<keyof CSVHeaders, number>> = {};
    headers.forEach((header, index) => {
        if (requiredHeaders.includes(header as keyof CSVHeaders)) {
            headerIndex[header as keyof CSVHeaders] = index;
        }
    });

    // Validate each data row
    for (let i = 1; i < lines.length; i++) {
        const lineNumber = i + 1;
        const values: string[] = parseCSVLine(lines[i]);
        
        // Check column count
        if (values.length < headers.length) {
            return { 
                valid: false, 
                error: `Line ${lineNumber}: Expected ${headers.length} columns, got ${values.length}` 
            };
        }

        const getValue = (field: keyof CSVHeaders): string => {
            const idx = headerIndex[field];
            return idx !== undefined && idx < values.length 
                ? (values[idx]?.trim() ?? '') 
                : '';
        };

        // Rule 1: name - required, non-empty string
        const name = getValue('name');
        if (!name) {
            return { valid: false, error: `Line ${lineNumber}: 'name' cannot be empty` };
        }

        // Rule 2-4: contact fields - optional strings (no format validation)
        // whatsapp_contact, sms_contact, viber_contact can be null/empty - no action needed

        // Rule 5: whatsapp, sms, viber - must be y/Y/n/N if provided
        const toggleFields: Array<keyof CSVHeaders> = ['whatsapp', 'sms', 'viber'];
        for (const field of toggleFields) {
            const value = getValue(field);
            if (value && !/^[yn]$/i.test(value)) {
                return { 
                    valid: false, 
                    error: `Line ${lineNumber}: '${field}' must be 'y', 'Y', 'n', or 'N', got '${value}'` 
                };
            }
        }

        // Rule 6: email - required, non-empty, basic format validation
        const email = getValue('email');
        if (!email) {
            return { valid: false, error: `Line ${lineNumber}: 'email' cannot be empty` };
        }
        if (!isValidEmail(email)) {
            return { 
                valid: false, 
                error: `Line ${lineNumber}: Invalid email format '${email}'` 
            };
        }
    }

    return { valid: true };
};

/**
 * Parses a single CSV line respecting quoted fields with embedded commas/quotes
 */
const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];
        
        if (char === '"') {
            if (inQuotes && nextChar === '"') {
                // Handle escaped quote ""
                current += '"';
                i++; // Skip next quote
            } else {
                // Toggle quote state
                inQuotes = !inQuotes;
            }
        } else if (char === ',' && !inQuotes) {
            // Field delimiter outside quotes
            result.push(current);
            current = '';
        } else {
            current += char;
        }
    }
    result.push(current); // Push final field
    
    return result;
};

/**
 * Basic email format validation using regex
 */
const isValidEmail = (email: string): boolean => {
    // Simple but effective pattern for most use cases
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};